import type { Ref } from 'vue'

interface MenuItemLite {
  id: number | string
  price: number
}

type NewOrderForm = {
  type: 'Dine-in' | 'Takeaway' | 'Delivery'
  tableId: number | string | null
  customerName: string
  notes: string
  items: Array<{
    menu_item_id: number
    variant_id?: number | null
    quantity: number
    notes?: string
    special_instructions?: string
    unit_price?: number
  }>
}

interface UseNewOrderModalBootstrapOptions {
  form: Ref<NewOrderForm>
  menuItems: Ref<MenuItemLite[]>
  tableId: Ref<number | null | undefined>
  posMode: Ref<boolean>
  fetchMenuItemsRequest: (
    onSuccess: () => void,
    onError: (errorMsg: string) => void
  ) => Promise<void>
  fetchAvailableTablesRequest: (
    tableId: number | null | undefined,
    onTableSelected: (tableId: number | string | null) => void,
    onError: (errorMsg: string) => void
  ) => Promise<void>
  collapseAllCategories: () => void
  showMenuError: () => void
  showTablesError: () => void
}

export function useNewOrderModalBootstrap(options: UseNewOrderModalBootstrapOptions) {
  const fetchMenuItems = async () => {
    await options.fetchMenuItemsRequest(
      () => options.collapseAllCategories(),
      () => options.showMenuError()
    )
  }

  const initializeFormItemsFromMenu = () => {
    options.form.value.items = options.menuItems.value.map((item) => ({
      menu_item_id: Number(item.id),
      variant_id: null,
      quantity: 0,
      notes: '',
      special_instructions: '',
      unit_price: item.price
    }))
  }

  const applyOpenTableSelection = () => {
    if (options.tableId.value && !options.posMode.value) {
      options.form.value.type = 'Dine-in'
      options.form.value.tableId = options.tableId.value
    }
  }

  const fetchAvailableTables = async () => {
    await options.fetchAvailableTablesRequest(
      options.tableId.value,
      (tableId) => {
        options.form.value.type = 'Dine-in'
        options.form.value.tableId = tableId
      },
      () => options.showTablesError()
    )
  }

  const runInitialLoad = async (hydrateOnMountedIfEditing: () => void) => {
    await Promise.all([fetchMenuItems(), fetchAvailableTables()])

    if (options.menuItems.value.length > 0) {
      initializeFormItemsFromMenu()
    }

    hydrateOnMountedIfEditing()
  }

  return {
    fetchMenuItems,
    initializeFormItemsFromMenu,
    applyOpenTableSelection,
    fetchAvailableTables,
    runInitialLoad
  }
}
